/**
 * =============================================================================
 * APPLICATION CONFIGURATION UTILITIES
 * =============================================================================
 * 
 * Centralized access to environment variables and application configuration.
 * Provides type-safe access to configuration values with validation.
 * 
 * =============================================================================
 */

/**
 * Get the current tenant ID from environment variables
 * In a real application, this would typically come from:
 * - User authentication context
 * - Route parameters
 * - User session data
 * 
 * For development, it's configured via VITE_TENANT_ID environment variable
 * 
 * @returns The tenant ID string
 * @throws Error if VITE_TENANT_ID is not configured
 */
export function getTenantId(): string {
  const tenantId = import.meta.env.VITE_TENANT_ID
  
  if (!tenantId) {
    throw new Error(
      'VITE_TENANT_ID environment variable is not configured. ' +
      'Please add it to your .env.local file.'
    )
  }
  
  return tenantId
}

/**
 * Get the API base URL from environment variables
 *
 * @returns The API base URL string
 */
export function getApiBaseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787'
}

/**
 * Get the Wallet API base URL from environment variables
 *
 * @returns The Wallet API base URL string
 */
export function getWalletApiBaseUrl(): string {
  return import.meta.env.VITE_WALLET_API_BASE_URL || 'http://localhost:8788'
}

/**
 * Check if the application is running in development mode
 * 
 * @returns True if in development mode
 */
export function isDevelopment(): boolean {
  return import.meta.env.DEV
}

/**
 * Check if the application is running in production mode
 * 
 * @returns True if in production mode
 */
export function isProduction(): boolean {
  return import.meta.env.PROD
}

/**
 * Get the application environment name
 * 
 * @returns The environment name (development, staging, production, etc.)
 */
export function getEnvironment(): string {
  return import.meta.env.VITE_APP_ENV || 'development'
}

/**
 * Get the Mapbox access token from environment variables
 *
 * @returns The Mapbox access token string
 * @throws Error if VITE_MAPBOX_ACCESS_TOKEN is not configured
 */
export function getMapboxAccessToken(): string {
  const token = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN

  if (!token) {
    throw new Error(
      'VITE_MAPBOX_ACCESS_TOKEN environment variable is not configured. ' +
      'Please add it to your .env.local file.'
    )
  }

  return token
}


/**
 * =============================================================================
 * FEATURE FLAGS
 * =============================================================================
 */

import type { FeatureFlag, FeatureFlagResult } from '@/types/featureFlags'
import { FEATURE_FLAG_REGISTRY } from '@/types/featureFlags'

/**
 * Get the value of a feature flag from environment variables
 *
 * @param flagName The name of the feature flag
 * @returns True if the feature is enabled, false otherwise
 */
export function getFeatureFlag(flagName: string): boolean {
  const config = FEATURE_FLAG_REGISTRY[flagName]
  if (!config) return false

  const value = import.meta.env[config.envKey]

  // Convert string values to boolean
  if (typeof value === 'string') {
    return value.toLowerCase() === 'true'
  }

  // Return default value if not set
  return config.defaultValue
}

/**
 * Get detailed information about a feature flag
 *
 * @param flagName The name of the feature flag
 * @returns Detailed feature flag evaluation result or null if flag doesn't exist
 */
export function getFeatureFlagResult(flagName: string): FeatureFlagResult | null {
  const config = FEATURE_FLAG_REGISTRY[flagName]
  if (!config) return null

  const value = import.meta.env[config.envKey]
  const isEnabled = getFeatureFlag(flagName)
  const source = typeof value === 'string' ? 'environment' : 'default'

  return {
    flagName: flagName as FeatureFlag,
    isEnabled,
    source,
    environment: getEnvironment()
  }
}

/**
 * Get all enabled features for debugging purposes
 *
 * @returns Array of enabled feature flag names
 */
export function getEnabledFeatures(): FeatureFlag[] {
  const allFlags: FeatureFlag[] = []

  return allFlags.filter(flag => getFeatureFlag(flag))
}

/**
 * Check if a feature is enabled and log a warning if disabled
 * Useful for development debugging
 *
 * @param flagName The feature flag to check
 * @param featureName Human-readable feature name for logging
 * @returns True if the feature is enabled
 */
export function isFeatureEnabledWithLog(flagName: string, featureName: string): boolean {
  const isEnabled = getFeatureFlag(flagName)

  if (!isEnabled && isDevelopment()) {
    console.warn(`🚫 Feature "${featureName}" is disabled in current environment`)
  }

  return isEnabled
}